import { useState, useEffect, useCallback } from "react";
import {
  allMenuItems,
  loadMenuVisibility,
  loadMenuVisibilityFromApi,
  saveMenuVisibilityToApi,
  type MenuVisibility,
} from "@/config/sidebarMenu";

export default function MenuVisibilityPanel() {
  const [visibility, setVisibility] = useState<MenuVisibility>(loadMenuVisibility);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMenuVisibilityFromApi().then(setVisibility);
  }, []);

  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 2000);
      return () => clearTimeout(t);
    }
  }, [saved]);

  const toggle = useCallback((id: string) => {
    setVisibility((prev) => {
      const next = { ...prev, [id]: prev[id] === false ? true : false };
      const parent = allMenuItems.find((m) => m.children?.some((c) => c.id === id));
      if (!parent) {
        const item = allMenuItems.find((m) => m.id === id);
        if (item?.children && next[id] === false) {
          for (const child of item.children) {
            next[child.id] = false;
          }
        }
      }
      return next;
    });
    setSaved(false);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    await saveMenuVisibilityToApi(visibility);
    setSaving(false);
    setSaved(true);
    window.dispatchEvent(new Event("floraiq_menu_changed"));
  }, [visibility]);

  const handleReset = useCallback(async () => {
    const defaults: MenuVisibility = {};
    for (const item of allMenuItems) {
      defaults[item.id] = true;
      if (item.children) {
        for (const child of item.children) {
          defaults[child.id] = true;
        }
      }
    }
    setVisibility(defaults);
    setSaving(true);
    await saveMenuVisibilityToApi(defaults);
    setSaving(false);
    setSaved(true);
    window.dispatchEvent(new Event("floraiq_menu_changed"));
  }, []);

  const alwaysVisible = new Set(["dashboard", "configuracoes"]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground-900">
            Itens do menu lateral
          </h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            Controle quais módulos aparecem no menu de navegação. Dashboard e
            Configurações são sempre visíveis.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-xs font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors"
          >
            Restaurar padrão
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
          >
            {saving ? "Salvando..." : saved ? "Salvo!" : "Aplicar"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-background-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-background-50 border-b border-background-200">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Item
              </th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wider w-24">
                Visível
              </th>
            </tr>
          </thead>
          <tbody>
            {allMenuItems.map((item) => {
              const locked = alwaysVisible.has(item.id);
              const parentHidden = visibility[item.id] === false;
              return (
                <tr key={item.id} className="group">
                  <td colSpan={2} className="p-0">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-background-100">
                      <div className="flex items-center gap-2.5">
                        <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                          <i className={`${item.icon} text-base`} />
                        </span>
                        <span
                          className={`font-medium ${
                            parentHidden
                              ? "text-foreground-400 line-through"
                              : "text-foreground-800"
                          }`}
                        >
                          {item.label}
                        </span>
                        {locked && (
                          <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">
                            fixo
                          </span>
                        )}
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={visibility[item.id] !== false}
                          onChange={() => toggle(item.id)}
                          disabled={locked}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-9 h-5 rounded-full transition-colors ${
                            locked
                              ? "bg-primary-300 cursor-not-allowed"
                              : "bg-background-300 peer-checked:bg-primary-500 peer-focus:ring-2 peer-focus:ring-primary-200"
                          }`}
                        >
                          <div
                            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              visibility[item.id] !== false
                                ? "translate-x-4"
                                : "translate-x-0"
                            }`}
                          />
                        </div>
                      </label>
                    </div>

                    {item.children && (
                      <div className="bg-background-50/50">
                        {item.children.map((child) => {
                          const childDisabled = parentHidden;
                          return (
                            <div
                              key={child.id}
                              className="flex items-center justify-between pl-12 pr-4 py-2 border-b border-background-100 last:border-b-0"
                            >
                              <span
                                className={`text-xs ${
                                  visibility[child.id] === false || childDisabled
                                    ? "text-foreground-400 line-through"
                                    : "text-foreground-700"
                                }`}
                              >
                                {child.label}
                              </span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={
                                    visibility[child.id] !== false && !childDisabled
                                  }
                                  onChange={() => toggle(child.id)}
                                  disabled={childDisabled}
                                  className="sr-only peer"
                                />
                                <div
                                  className={`w-9 h-5 rounded-full transition-colors ${
                                    childDisabled
                                      ? "bg-background-200 cursor-not-allowed"
                                      : "bg-background-300 peer-checked:bg-primary-500 peer-focus:ring-2 peer-focus:ring-primary-200"
                                  }`}
                                >
                                  <div
                                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                      visibility[child.id] !== false && !childDisabled
                                        ? "translate-x-4"
                                        : "translate-x-0"
                                    }`}
                                  />
                                </div>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
